import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Link } from '@tanstack/router'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useDimensions from '../../hooks/useDimensions'
import useRefreshBalance from '../../hooks/useRefreshBalance'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import closeModal from '../../utils/closeModal'
import { numberWithCommas } from '../../utils/number'
import { Spinner } from '../common'
import Modal from '../common/modal'
import ProfileDrawer from './profile-drawer'

interface PointsObj {
  available_points: number
  total_points: number
}

const RedeemModal = () => {
  const { refreshBalance } = useRefreshBalance()
  const setIsCoinAnimVisible = useBoundStore(
    state => state.common.setIsCoinAnimVisible,
  )
  const modal = useModal()
  const profileDrawerModal = useModal(ProfileDrawer)
  const { t } = useTranslation([
    'cashier',
    'referral',
    'vip',
    'walletManagement',
  ])
  const { isDesktop } = useDimensions()
  const [amount, setAmount] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [points, setPoints] = useState<PointsObj>({
    available_points: 0,
    total_points: 0,
  })

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    if (modal.visible) {
      document.body.classList.add('noscroll')

      api
        .post(
          '/conduct.php',
          {
            type: 'get_available_points',
          },
          { signal },
        )
        .then(res => {
          const { status, info } = res.data as ApiResponse<PointsObj>
          if (status === 1) {
            setPoints(info)
          }
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            console.error(error)
          }
        })
    } else {
      setAmount('')
    }

    return () => {
      controller.abort()
    }
  }, [modal.visible])

  const redeem = async () => {
    try {
      setErrorMessage('')
      setIsLoading(true)
      const res = await api.post<ApiResponse<string>>('/conduct.php', {
        type: 'new_apply_point',
        num: amount,
      })
      const { status, info } = res.data
      if (status === 1) {
        refreshBalance()
        setIsCoinAnimVisible(true)
        closeModal(modal)
      } else {
        setErrorMessage(info)
      }
    } catch (error) {
      console.error(error)
    }
    setIsLoading(false)
  }

  return (
    <Modal
      id="redeem-modal"
      containerStyle="bg-refer-now bg-contain bg-repeat bg-transparent aspect-[585_/_837] w-[350px] pt-[85px] pb-10 px-[60px] !border-0 xl:w-[450px] xl:pt-[95px] xl:pb-10 xl:px-[70px]">
      <div className="flx-btw-ctr mb-3">
        <p className="text-[15px] font-Avenir-Medium text-txt text-center xl:text-[17px]">
          {t('accumulated-points', { ns: 'vip' })}:
        </p>
        <p className="text-[15px] font-Avenir-Medium text-center xl:text-[17px] text-[#fbb03b]">
          {numberWithCommas(+points.total_points)}
        </p>
      </div>
      <div className="flx-btw-ctr mb-3">
        <p className="text-[15px] font-Avenir-Medium text-txt text-center xl:text-[17px]">
          {t('available-points', { ns: 'vip' })}:
        </p>
        <p className="text-[15px] font-Avenir-Medium text-center xl:text-[17px] text-[#fbb03b]">
          {numberWithCommas(+points.available_points)}
        </p>
      </div>
      <div className="flx-btw-ctr mb-3">
        <p className="text-[15px] font-Avenir-Medium text-txt text-center xl:text-[17px]">
          {t('redeemable-points', { ns: 'vip' })}:
        </p>
        <p className="text-[15px] font-Avenir-Medium text-center xl:text-[17px] text-[#fbb03b]">
          {numberWithCommas(Math.floor(+points.available_points / 1000) * 1000)}
        </p>
      </div>
      {errorMessage && (
        <p className="text-sm text-[rgb(255,64,88)] w-full xl:text-base">
          {errorMessage}
        </p>
      )}
      <input
        className="text-[15px] font-Avenir-Medium text-primary rounded w-full bg-white mt-3 mb-[150px] px-3 py-1.5 xl:text-[17px] xl:mt-6 xl:mb-[180px]"
        placeholder="1000"
        value={amount}
        onChange={e => setAmount(e.target.value)}
        type="number"
        inputMode="numeric"
      />
      <div className="flx-ctr" style={{ marginTop: '25px' }}>
        {isLoading ? (
          <Spinner containerStyle={{ height: 38.5 }} />
        ) : (
          <button
            className={classNames(
              'block text-center bg-[#df0033] text-[white] transition-all duration-[0.2s] ease-[ease-in-out] cursor-pointer',
              'uppercase text-sm font-Syncopate-Bold w-full px-6 py-3 rounded-sm disabled:bg-[#65676a] disabled:text-[#d9d9d9] xl:text-base',
            )}
            onClick={redeem}
            disabled={+amount > +points.available_points || +amount <= 0}>
            {t('redeem', { ns: 'referral' })}
          </button>
        )}
      </div>
      <Link to="/profile/financial-transaction" search={{ tab: 'all' }}>
        <button
          className="text-xs underline text-center block text-txt mt-4 mb-0 mx-auto xl:text-sm"
          onClick={() => {
            if (!isDesktop) {
              closeModal(profileDrawerModal)
            }
            closeModal(modal)
          }}>
          {t('transaction-history', { ns: 'cashier' })}
        </button>
      </Link>
    </Modal>
  )
}

export default NiceModal.create(RedeemModal)
